import { FieldLocaleType } from "@/types/app";
import {
  ICardData,
  ICategoryInfo,
  IPCMembership,
  IPCPromoCodeCampaignList,
  IProductCategory,
  IProductSlotPrice,
  ISmeetzPicture,
  PCTypes,
  PC_SELL_ON_SOLD_OUT,
  ProductStatus,
  QuestionType,
  RetailOptionType,
  isSeatingParentType,
} from "@/types/db/product-category";
import { SiteLanguages } from "../constants";
import {
  countTicketGroupsTicketsPCs,
  getProductCategoriesIdsByPriceIds,
  getTicketGroupsTicketsPCsWithSeatingPlanData,
} from "@/api/gql/ticket-group";
import { IKeyValue, ISyncField, ISyncUpdates } from "@/types/db/timeslots";
import {
  createCroppedImageproxyUrl,
  createImageproxyUrl,
  SmeetzPictureEntityTypes,
  SmeetzPictureEntityTypeToTableMap,
} from "@/utils/helpers/pictures";
import { sumArray } from "@/utils/helpers/common";
import _cloneDeep from "lodash/cloneDeep";
import { getLanguage } from "@/utils/helpers/lang";
import { CARD_IMAGE_HEIGHT, CARD_IMAGE_WIDTH } from "@/constants/style";
import dayjs from "dayjs";
import { getDeserializedCustomFieldQuestions } from "@/api/custom-fields/custom-fields";
import POSConfigModule from "@/store/modules/pos-config";
import { LogLevel, LogService } from "@/services/logging";
import { ITeamSetting } from "@/interfaces/teams/teams";
import CommonModule from "@/store/modules/common";
import {
  getHandshakeCategoryTspropertyList,
  getPCListWithSeatingPlanData,
  pullParentPSCs,
  pullParentPSCsWithDetails,
} from "@/api/gql/product-category";
import { ProductBranding } from "@/types/gql/generated/graphql";
import { cloneDeep } from "lodash";
import { getSmeetzPictures } from "@/api/gql/common";
import { dexieDB } from "@/services/dexie-database";
import {
  BookingStatus,
  IProductSlotState,
} from "@/types/db/product-slot-state";
import { getUpdatedRetailPrices } from "@/api/gql/product-slot-prices";
import { toLocalProductCategories3 } from "../vue-helpers/product-category";
import {
  bulkUpsertProductCategories,
  getProductCategoriesByIds,
  getProductCategoriesByPriceIds,
  queryProductCategoriesByTicketGroup,
  getProductCategoriesByStockIds,
  getAllSeatingChildPCs,
} from "@/dexie/product-category";
import { pruneStaleRecords } from "@/dexie/helpers";
import shoppingIcon from "@/assets/images/icons/shopping-icon.png";

/**
 * // Fetches all product categories of a given array of ticket groups
 * @param ids Ticket group ids
 */
export async function fetchAllTicketGroupsPCs(
  ids: number[],
  signal?: AbortSignal
) {
  // check if the app is online before start fetching
  if (!window.navigator.onLine) {
    LogService.info(`App is not online, stop fetching ticketGroups`);
    return;
  }

  ////
  // Lets first get the count of timeslots
  const count = await countTicketGroupsTicketsPCs(ids);
  const pcsCount = count.productsCategories ? count.productsCategories : 1;
  /////

  let limit = 50;
  if (POSConfigModule.groupId === 19017 /*Locarno*/) {
    limit = 10;
  }

  let skip = 0;

  const seatingPSCsChildren: { childPSCId: number; parentPSCId: number }[] = [];
  const freshPCIds = new Set<number>();

  // Clear all existing ProductLogo pictures
  await dexieDB.pictures
    .where({ entityType: SmeetzPictureEntityTypes.ProductLogo })
    .delete();

  // Clear all existing TicketLogo pictures
  await dexieDB.pictures
    .where({ entityType: SmeetzPictureEntityTypes.TicketLogo })
    .delete();

  // eslint-disable-next-line
  while (true) {
    if (signal?.aborted) break;
    const ticketGroups = await getTicketGroupsTicketsPCsWithSeatingPlanData(
      ids,
      { skip, limit }
    );
    let pcs = ticketGroups.productsCategories;
    // break loop when no more slots are available
    if (!pcs || !pcs.length) {
      break;
    }

    // update the offset before filtering
    skip += pcs.length;

    // consider only not-cancelled pcs and live products
    pcs = pcs.filter(
      (pc) =>
        pc?.productCategory?.cancelled === 0 &&
        pc?.productCategory?.product?.status === ProductStatus.LIVE
    );

    // going to convert to Local ProductCategories
    const localPCs = await toLocalProductCategories2(pcs);

    const localPCIds = localPCs.map((pc) => pc.id);
    // fetch the Skidata Handshake properties (to be used when creating the qrcode)
    const handshakeCategoryTspropertyList =
      await getHandshakeCategoryTspropertyList(localPCIds);

    // Build Map for O(1) lookup instead of O(N*M) with .find()
    const handshakeMap = new Map(
      handshakeCategoryTspropertyList.map((config) => [
        Number(config?.categoryId),
        config,
      ])
    );

    for (const pc of localPCs) {
      const skidataConfig = handshakeMap.get(pc.id);
      pc.handshakeCategoryTsproperty = skidataConfig
        ? {
            areaCode: String(skidataConfig?.areaCode),
            areaName: String(skidataConfig?.areaName),
            ticketTypeCode: String(skidataConfig?.ticketTypeCode),
            ticketTypeName: String(skidataConfig?.ticketTypeName),
          }
        : null;
    }

    // Write PCs to Dexie per-page (streaming, memory-friendly)
    await bulkUpsertProductCategories(localPCs);
    for (const pc of localPCs) {
      freshPCIds.add(pc.id);
    }

    // fetch and store all the product logos (pictures) to be printed on the ticket
    const productIds = localPCs.map((pc) => pc.product.id);
    await fetchAndStoreSmeetzPictures({
      entityType: SmeetzPictureEntityTypes.ProductLogo,
      entityIds: productIds,
    });

    // fetch and store all the ticket (PC) logos (pictures) to be printed on the ticket
    await fetchAndStoreSmeetzPictures({
      entityType: SmeetzPictureEntityTypes.TicketLogo,
      entityIds: localPCIds,
    });

    // if a PSC is a seating child (sub-category) then we will need to track the info for later to be able to pull parent category data
    localPCs.forEach((lpc) => {
      if (lpc.type === PCTypes[PCTypes.SeatedSub] && lpc.seatingParentPSCId) {
        seatingPSCsChildren.push({
          childPSCId: lpc.id,
          parentPSCId: lpc.seatingParentPSCId,
        });
      }
    });

    // Why 50? because we decided that timeslots progress is going to be 50% from the bar
    const progress = Math.round((pcs.length * 50) / pcsCount);
    POSConfigModule.setPCsLoaded(POSConfigModule.pcsLoaded + progress);
  }

  LogService.info(
    `Done with pulling PSCs from remote and inserting to indexedDB, going to check if we have seating children PSCs`
  );
  // If aborted, skip enrichment and pruning — partial data is safe
  if (signal?.aborted) return;

  // Enrich seating sub-categories with parent data (now in Dexie, read back for enrichment)
  if (seatingPSCsChildren.length) {
    LogService.info(
      `We have sub-categories attached that need their parent data for seating plan logic`,
      {
        params: seatingPSCsChildren,
      }
    );
    // 1- Pull parent categories from remote
    let parentPSCs: any;
    try {
      parentPSCs = await pullParentPSCs(
        seatingPSCsChildren.map((item) => item.parentPSCId)
      );
      parentPSCs.productCategory = parentPSCs.productCategory?.filter(
        (parentPC: any) =>
          parentPC.cancelled === 0 &&
          parentPC.product.status === ProductStatus.LIVE
      );
    } catch (err) {
      LogService.info(
        `Error when pulling seating parent categories`,
        err,
        LogLevel.Error
      );
    }
    // 2- attach info from parent categories to sub-categories that are now in Dexie
    let updatedSubCategoriesObjects: IProductCategory[] = [];
    try {
      updatedSubCategoriesObjects = await getAndUpdateSubCategories(
        seatingPSCsChildren,
        parentPSCs.productCategory
      );
    } catch (err) {
      LogService.info(
        `Error when getting sub-categories from localDB`,
        err,
        LogLevel.Error
      );
    }
    // 3- update sub-categories in Dexie
    try {
      await bulkUpsertProductCategories(updatedSubCategoriesObjects);
    } catch (err) {
      LogService.info(
        `Error when updating sub-categories into localDB`,
        err,
        LogLevel.Error
      );
    }
  } else {
    LogService.info(
      `Done with pulling PSCs from remote and inserting to indexedDB`
    );
  }

  // Prune any stale PCs from the previous config that are no longer present
  await pruneStaleRecords(dexieDB.productCategories as any, freshPCIds);
}

/**
 * 1- Fetches all seating plan parent product categories of a given array of pcIds
 * 2- Converts them into local format
 * 3- Attaches to them an array of their sub-categories
 * 4- Insert them in indexDB
 * @param ids product categories ids
 */
export async function fetchConfigSeatingPlanParentPCs() {
  try {
    // Get child PCs that have a seating parent using indexed query (avoids full table scan)
    const dbPCs = await getAllSeatingChildPCs();

    if (!dbPCs.length) {
      return;
    }

    // Deduplicate parent IDs before the network call
    const parentPCsIds = [
      ...new Set(dbPCs.map((pc) => pc.seatingParentPSCId!)),
    ];

    // Fetch parent PCs from remote db using their Ids
    const parentPCs = await pullParentPSCsWithDetails(parentPCsIds);

    parentPCs.productCategory = parentPCs.productCategory?.filter(
      (parentPC: any) =>
        parentPC.cancelled === 0 &&
        parentPC.product.status === ProductStatus.LIVE
    );

    if (!parentPCs.productCategory?.length) {
      return;
    }

    // Convert parent PCs into local format
    const localFormatParentPCs = await convertParentPCsToLocalFormat(parentPCs);

    // Map of parentId -> child PCs
    const childrenByParent = new Map<number, IProductCategory[]>();
    for (const pc of dbPCs) {
      const parentId = pc.seatingParentPSCId!;
      const children = childrenByParent.get(parentId);
      if (children) {
        children.push(pc);
      } else {
        childrenByParent.set(parentId, [pc]);
      }
    }

    // Attach sub-categories and ticket groups to each parent PC
    for (const parentPC of localFormatParentPCs) {
      const subCategories = childrenByParent.get(parentPC.id) || [];
      parentPC.subCategories = subCategories;
      parentPC.ticketGroups = [
        ...new Set(subCategories.map((pc) => pc.ticketGroups).flat()),
      ];
    }

    // Insert parent PCs in localDB
    await bulkUpsertProductCategories(localFormatParentPCs);
  } catch (error) {
    LogService.info(
      "Error in fetchConfigSeatingPlanParentPCs",
      { error },
      LogLevel.Error
    );
  }
}

/**
 * Returns products categories fetched from GetTicketGroupsTicketsPCsQuery in local
 * IProductCategory format.
 * Note: Removes duplicates as well.
 * @param productCategories
 * @param retailTaxes is an array of general VATs that will be assigned to retail product
 *
 */
export async function toLocalProductCategories2(
  // TODO Hamza: remove the use of "any" type and use the generated types commented below, a TS error will be shown
  // saying that seatingParentPSCId doesn't exist on type TTicketGroupsProductCategoriesWithSeatingPlan althoug it does exist
  // you need to fix it and remove the use of "any" type inside Array functions used in within this function
  productCategories: any /*TTicketGroupsProductCategories | TTicketGroupsProductCategoriesWithSeatingPlan*/
): Promise<IProductCategory[]> {
  // Transform fetched product categories to local db format IProductCategory
  if (!productCategories) {
    return [];
  }

  const pcs: IProductCategory[] = [];
  const seenIds = new Set<number>();
  for (const rpc of productCategories) {
    if (!rpc || !rpc.productCategory) {
      continue;
    }
    if (isSeatingParentType(rpc.productCategory.type)) {
      // Don't convert and insert seating parent category in indexedDB
      // Info needed from this seating parent category will be pulled later and attached to its seating child categories
      continue;
    }

    const {
      id,
      name,
      description,
      address,
      type,
      ticketGroups,
      prices,
      product,
      translations,
      qrCodeType,
      multipleScan,
      teamSettingList,
      saleStartType,
      saleStart,
      saleEndType,
      saleEnd,
      salesEndDurationMinutes,
      salesStartDurationDays,
      timeSlotCount,
      seatingParentPSCId,
      resource,
      seatsCat,
      categoryInfoList,
    } = rpc.productCategory;

    const categoryProduct = {
      id: product?.id as number,
      isIdivTax: product?.isIdivTax as number,
      name: (product && product.name) || "",
      nameLocale: extractTranslation("name", product?.translations),
      currency: (product && product.currency) || "",
      status: product?.status as number,
      pictureUrl:
        product && product.pictures
          ? product.pictures.find(
              (p: any) => p && p.picture && p?.picture.isPrimary
            )?.picture?.realUrl || ""
          : "",

      productBrandings: product?.productBrandingList
        ? product?.productBrandingList.map((pb: any) => {
            return {
              id: pb.Id,
              customText: pb.CustomText || pb.customText || "",
              description1: pb.Description1 || "",
              description2: pb.Description2 || "",
              description3: pb.Description3 || "",
            };
          })
        : [],
      customTicketFields: product?.customTicketFieldsList
        ? product?.customTicketFieldsList
            .filter(
              (c: any) =>
                // Fifth bit should be always on
                (10000 & Number(c.visibleFor)) === 16 && c.canceled !== 1
            )
            .map((c: any) => {
              return {
                id: c.id,
                answerExample: c.answerExample,
                cancelled: c.canceled,
                informationType: c.informationType,
                list:
                  c.questionType === QuestionType.DROPDOWN ||
                  c.questionType === QuestionType.LIST ||
                  c.questionType === QuestionType.RADIO
                    ? c.list
                    : [], //c.list,
                name: c.name,
                position: c.position,
                productId: c.productId,
                questionType: c.questionType,
                validation: c.validation,
                visibleFor: c.visibleFor,
                prices: c.prices.map((p: any) => p.priceId),
              };
            })
        : [],
      customBookingFields: product?.customBookingFieldsList
        ? product?.customBookingFieldsList
            .filter(
              (c: any) =>
                // Fifth bit should be always on
                (10000 & Number(c.visibleFor)) === 16 && c.canceled !== 1
            )
            .map((c: any) => {
              return {
                id: c.id,
                answerExample: c.answerExample,
                cancelled: c.canceled,
                informationType: c.informationType,
                list:
                  c.questionType === QuestionType.DROPDOWN ||
                  c.questionType === QuestionType.LIST ||
                  c.questionType === QuestionType.RADIO
                    ? c.list
                    : [], //c.list,
                name: c.name,
                position: c.position,
                productId: c.productId,
                questionType: c.questionType,
                validation: c.validation,
                visibleFor: c.visibleFor,
              };
            })
        : [],
    };

    // we need to show only prices that are meant to be sold on POS
    const pricesToSell = prices?.filter(
      (p: any) =>
        // [16, 17, 18, 20, 21, 22, 23, 24, 31].includes(Number(p?.availability))
        // Fifth bit to the left should be on
        (10000 & Number(p?.availability)) === 16
    );

    const categoryPrices: IProductSlotPrice[] = pricesToSell?.length
      ? // ensure prices compatibility
        (pricesToSell
          .filter((pr: any) => {
            /**
             * filter out prices that are:
             * 1- canceled
             * 2- have retail options but all of them are deleted
             */
            if (pr?.cancelled === 1) {
              return false;
            }
            if (
              pr?.retailOptions &&
              pr.retailOptions.length > 0 &&
              !pr.retailOptions.some((o: any) => !o.deletedAt)
            ) {
              return false;
            }
            return true;
          })
          .map((p: any) => {
            return {
              name: p?.name || "",
              id: p?.id as number,
              nameLocale: extractTranslation("priceName", p?.translations),
              description: p?.description || "",
              descriptionLocale: extractTranslation(
                "description",
                p?.translations
              ),
              // If this price is actually a retail then we consider its priceOnsite and use its value everywhere
              value:
                p?.retailOptions &&
                p?.retailOptions.length > 0 &&
                p?.priceOnsite
                  ? p?.priceOnsite
                  : p?.value,
              availability: p?.availability,
              priceOnsite: !p?.priceOnsite ? 0 : p?.priceOnsite,
              quantity: p?.quantity,
              totalQuantity: p?.totalQuantity,
              // We will not try to assign the field a null value
              ...(p?.vatRateGeneralId
                ? { vatRateGeneralId: p?.vatRateGeneralId }
                : null),

              taxes: p?.tax ? [p?.tax] : [],
              addons: (p?.addons || []).filter((a: any) => a?.canceled === 0),
              updatedAt: p?.updatedAt,
              isEditable: p?.isEditable,
              priceCategory: p?.priceCategory,
              sortPrice: p?.sortPrice,
              retail: p?.retailOptions
                ?.filter((o: any) => !o?.deletedAt)
                ?.map((o: any) => {
                  return {
                    id: o?.id,
                    inventoryAlert: o?.inventoryAlert,
                    name: o?.name,
                    nameLocale: JSON.parse(o?.nameLocale) as FieldLocaleType,
                    type: o?.type,
                    barcode: o?.barcode,
                    sku: o?.sku,
                    sellingSoldOut: o?.sellingSoldOut,
                    optionSort: o?.optionSort ?? 25,
                    smeetzPictures: o?.smeetzPictures?.map((s: any) => {
                      return {
                        id: s?.id,
                        name: s?.name,
                        metaData: s?.metaData,
                        // TODO Hamza now s?.entity is of type number | null | undefined even though it is string inside schemas/product-categories.ts and
                        // I ran yarn gql:generate many times without successfully getting it updated ---> Try again running yarn gql:generate
                        entity: String(s?.entity),
                        entityId: s?.entityId,
                      };
                    }),
                  };
                }),
              packageMinQuantity: p?.packageMinQuantity,
              packageMaxQuantity: p?.packageMaxQuantity,
            };
          }) as IProductSlotPrice[])
      : [];

    // If a given category has no prices, then no need to insert it into the DB
    if (!categoryPrices.length) {
      continue;
    }

    // lets build StockIds arrays
    const stockIds: number[] = [];
    // stock units one ticket consumes per stock (stock_category.quantity)
    const stockConsumptions: { [stockId: number]: number } = {};
    // For FlipLab we allow overbookings by adding -1000 to the shared stocks Ids
    if (
      rpc.productCategory.stockCategoryList &&
      rpc.productCategory.stockCategoryList.length
    ) {
      rpc.productCategory.stockCategoryList.forEach((s: any) => {
        if (s && s.stockRead && s.stockRead.id) {
          stockIds.push(s.stockRead.id);
          stockConsumptions[s.stockRead.id] = Number(s.quantity) || 1;
        }
      });

      if (POSConfigModule.isFlipLab) {
        // For FlipLab we allow overbookings by adding -1000 to the shared stocks Ids
        stockIds.push(-1000);
      }
    }

    // Extract barcodes from retail options for Dexie multi-entry index
    const barcodes: string[] = [];
    for (const price of categoryPrices) {
      if (price.retail) {
        for (const retailOption of price.retail) {
          if (retailOption.barcode && retailOption.barcode.trim() !== "") {
            barcodes.push(retailOption.barcode.trim());
          }
        }
      }
    }

    // Extract SKUs from retail options for Dexie multi-entry index
    const skus: string[] = [];
    for (const price of categoryPrices) {
      if (price.retail) {
        for (const retailOption of price.retail) {
          if (retailOption.sku && retailOption.sku.trim() !== "") {
            skus.push(retailOption.sku.trim());
          }
        }
      }
    }

    // Extract price IDs for Dexie multi-entry index
    const priceIds = categoryPrices.map((p) => p.id);

    const memberships: IPCMembership[] = rpc.productCategory.memberships?.length
      ? rpc.productCategory.memberships.map((m: any) => {
          return {
            id: Number(m?.id),
            startDate: m?.startDate
              ? dayjs.utc(m?.startDate as string).toISOString()
              : "",
            expiryDate: m?.expiryDate
              ? dayjs.utc(m?.expiryDate as string).toISOString()
              : "",
            type: Number(m?.type),
            expiryType: Number(m?.expiryType),
            periodLength: Number(m?.periodLength),
            numberMandatoryPeriod: Number(m?.numberMandatoryPeriod),
          };
        })
      : [];

    const promoCodeCampaignList: IPCPromoCodeCampaignList[] = rpc
      .productCategory.promoCodeCampaignList?.length
      ? rpc.productCategory.promoCodeCampaignList.map((c: any) => {
          return {
            id: Number(c.id),
            giftCardTicketId: Number(c.giftCardTicketId),
            startDateTime: String(c?.startDateTime),
            endDateTime: String(c?.endDateTime),
            expiryType: Number(c.expiryType),
            expiryDays: Number(c.expiryDays),
            cancelled: Number(c.cancelled),
          };
        })
      : [];

    let categoryTeamSetting: ITeamSetting | null = null;
    if (teamSettingList && teamSettingList[0]) {
      const countedPsp: number[] = JSON.parse(
        `[${teamSettingList[0].countedPsp}]`
      );
      categoryTeamSetting = {
        categoryId: Number(id),
        countedPsp,
        maxParticipants: Number(teamSettingList[0].maxParticipants),
      };
    }
    let categoryInfo: ICategoryInfo | null = null;
    if (categoryInfoList && categoryInfoList.length) {
      categoryInfo = {
        scanOpeningMinutesBeforeStart: Number(
          categoryInfoList[0].scanOpeningMinutesBeforeStart
        ),
      };
    }

    const pc: IProductCategory = {
      _id: String(id),
      id: id as number,
      name: name || "",
      seatsCat: seatsCat || "",
      description: description || "",
      type: PCTypes[Number(type)],
      address: address ? address : "",
      qrCodeType,
      multipleScan: multipleScan,
      nameLocale: extractTranslation("categoryName", translations),
      descriptionLocale: extractTranslation(
        "categoryDescription",
        translations
      ),
      ticketGroups: ticketGroups?.map((t: any) => t?.ticketGroupId) as number[],
      product: categoryProduct,
      prices: categoryPrices,
      stockIds: stockIds,
      stockConsumptions: stockConsumptions,
      barcodes: [...new Set(barcodes)], // Unique barcodes for Dexie multi-entry index
      skus: [...new Set(skus)], // Unique SKUs for Dexie multi-entry index
      priceIds: priceIds, // Price IDs for Dexie multi-entry index
      memberships: memberships,
      promoCodeCampaignList: promoCodeCampaignList,
      teamSetting: categoryTeamSetting,
      saleStartType,
      saleStart: saleStart ? dayjs.utc(saleStart).toISOString() : saleStart,
      saleEnd: saleEnd ? dayjs.utc(saleEnd).toISOString() : saleStart,
      saleEndType,
      salesEndDurationMinutes,
      salesStartDurationDays,
      timeSlotCount: timeSlotCount,
      isRecurring:
        (timeSlotCount && timeSlotCount > 1) || type === PCTypes.Recurring
          ? true
          : false,

      // Seating Plan logic
      seatingParentPSCId:
        Number(type) === PCTypes.SeatedSub ? seatingParentPSCId : null,
      // At this stage, below seating plan fields will be null until we pull Seating Parent PSC from backend (we will do so if this PSC type=31)
      isParentReccuringSeating: null,
      seatingParentResourceEventKey: null,
      seatingParentCategoryName: null,
      resource:
        resource && resource.quantity ? { quantity: resource.quantity } : null,
      categoryInfo,
    };

    // Make sure that we aren't adding any ProductCategory that was already added
    if (!seenIds.has(pc.id)) {
      seenIds.add(pc.id);
      pcs.push(pc);
    }
  }

  // If a PC has custom fields of type DROPDOWN/RADIO/LIST attached to it
  // then we need to pull its deserialized 'list' field
  const isListType = (questionType: number | undefined) =>
    questionType === QuestionType.DROPDOWN ||
    questionType === QuestionType.RADIO ||
    questionType === QuestionType.LIST;

  const customBookingListFields = pcs.flatMap((p) =>
    (p.product.customBookingFields || []).filter((c) =>
      isListType(c?.questionType)
    )
  );
  const customTicketListFields = pcs.flatMap((p) =>
    (p.product.customTicketFields || []).filter((c) =>
      isListType(c?.questionType)
    )
  );

  if (customBookingListFields.length || customTicketListFields.length) {
    const customBookingFieldsIds = customBookingListFields.map((c) =>
      Number(c?.id)
    );
    const customTicketFieldsIds = customTicketListFields.map((c) =>
      Number(c?.id)
    );

    const resp = await getDeserializedCustomFieldQuestions(
      Number(POSConfigModule.selectedConfig?.group),
      customTicketFieldsIds,
      customBookingFieldsIds
    );

    // Build Map<productId, PC[]> for O(1) lookups
    const pcsByProductId = new Map<number, IProductCategory[]>();
    for (const pc of pcs) {
      const existing = pcsByProductId.get(pc.product.id);
      if (existing) {
        existing.push(pc);
      } else {
        pcsByProductId.set(pc.product.id, [pc]);
      }
    }

    const deserializedCustomBookingFields = resp.data.customBookingFields;
    const deserializedCustomTicketFields = resp.data.customTicketFields;

    if (deserializedCustomBookingFields?.length) {
      for (const field of deserializedCustomBookingFields) {
        const matchingPCs = pcsByProductId.get(field.productId) || [];
        for (const pc of matchingPCs) {
          const cust = pc.product.customBookingFields?.find(
            (c) => c.id === field.id
          );
          if (cust) {
            cust.list = field.list;
          }
        }
      }
    }

    if (deserializedCustomTicketFields?.length) {
      for (const field of deserializedCustomTicketFields) {
        const matchingPCs = pcsByProductId.get(field.productId) || [];
        for (const pc of matchingPCs) {
          const cust = pc.product.customTicketFields?.find(
            (c) => c.id === field.id
          );
          if (cust) {
            cust.list = field.list;
          }
        }
      }
    }
  }
  return pcs;
}

/**
 * It takes a list of parent PCs and convert them into local format
 * This code was separated from the function toLocalProductCategories2 because it is related only to parent PCs of seating plans
 */
export async function convertParentPCsToLocalFormat(parentPCs: any) {
  const pcs: IProductCategory[] = [];
  const seenIds = new Set<number>();
  if (parentPCs && parentPCs.productCategory) {
    parentPCs = parentPCs.productCategory;
    for (let i = 0; i < parentPCs.length; i++) {
      if (!parentPCs[i]) {
        continue;
      }
      if (Number(parentPCs[i].type) === PCTypes.SeatedSub) {
        continue;
      }

      const {
        id,
        name,
        description,
        address,
        type,
        ticketGroups,
        prices,
        product,
        translations,
        qrCodeType,
        multipleScan,
        saleStartType,
        saleStart,
        saleEndType,
        saleEnd,
        salesEndDurationMinutes,
        salesStartDurationDays,
        timeSlotCount,
        resourceRead,
        isRecurringSeat,
      } = parentPCs[i];

      const categoryProduct = {
        id: product?.id as number,
        isIdivTax: product?.isIdivTax as number,
        name: (product && product.name) || "",
        nameLocale: extractTranslation("name", product?.translations),
        currency: (product && product.currency) || "",
        status: product?.status as number,
        pictureUrl:
          product && product.pictures
            ? product.pictures.find(
                (p: any) => p && p.picture && p?.picture.isPrimary
              )?.picture?.realUrl || ""
            : "",
        productBrandings: product?.productBrandingList
          ? product?.productBrandingList.map((pb: ProductBranding) => {
              return {
                id: pb.Id,
                customText: pb.CustomText || "",
                description1: pb.Description1 || "",
                description2: pb.Description2 || "",
                description3: pb.Description3 || "",
              };
            })
          : [],
        // Custom fields are not used for seating parent PCs
        customTicketFields: [],
        customBookingFields: [],
      };

      // we need to show only prices that are meant to be sold on POS
      const pricesToSell = prices?.filter(
        (p: any) =>
          // [16, 17, 18, 20, 21, 22, 23, 24, 31].includes(Number(p?.availability))
          // Fifth bit to the left should be on
          (10000 & Number(p?.availability)) === 16
      );

      const categoryPrices: IProductSlotPrice[] = pricesToSell?.length
        ? // ensure prices compatibility
          (pricesToSell
            .filter((pr: any) => {
              /**
               * filter out prices that are:
               * 1- canceled
               * 2- have retail options but all of them are deleted
               */
              if (pr?.cancelled === 1) {
                return false;
              }
              if (
                pr?.retailOptions &&
                pr.retailOptions.length > 0 &&
                !pr.retailOptions.some((o: any) => !o.deletedAt)
              ) {
                return false;
              }
              return true;
            })
            .map((p: any) => {
              return {
                name: p?.name || "",
                id: p?.id as number,
                nameLocale: extractTranslation("priceName", p?.translations),
                description: p?.description || "",
                descriptionLocale: extractTranslation(
                  "description",
                  p?.translations
                ),
                // If this price is actually a retail then we consider its priceOnsite and use its value everywhere
                value:
                  p?.retailOptions &&
                  p?.retailOptions.length > 0 &&
                  p?.priceOnsite
                    ? p?.priceOnsite
                    : p?.value,
                availability: p?.availability,
                priceOnsite: !p?.priceOnsite ? 0 : p?.priceOnsite,
                quantity: p?.quantity,
                // We will not try to assign the field a null value
                ...(p?.vatRateGeneralId
                  ? { vatRateGeneralId: p?.vatRateGeneralId }
                  : null),

                taxes: p?.tax ? [p?.tax] : [],
                addons: p?.addons,
                updatedAt: p?.updatedAt,
                isEditable: p?.isEditable,
                priceCategory: p?.priceCategory,
                sortPrice: p?.sortPrice,
                retail: p?.retailOptions
                  ?.filter((o: any) => !o?.deletedAt)
                  ?.map((o: any) => {
                    return {
                      id: o?.id,
                      inventoryAlert: o?.inventoryAlert,
                      name: o?.name,
                      nameLocale: JSON.parse(o?.nameLocale) as FieldLocaleType,
                      type: o?.type,
                      barcode: o?.barcode,
                      sellingSoldOut: o?.sellingSoldOut,
                      smeetzPictures: o?.smeetzPictures?.map((s: any) => {
                        return {
                          id: s?.id,
                          name: s?.name,
                          metaData: s?.metaData,
                          // TODO Hamza now s?.entity is of type number | null | undefined even though it is string inside schemas/product-categories.ts and
                          // I ran yarn gql:generate many times without successfully getting it updated ---> Try again running yarn gql:generate
                          entity: String(s?.entity),
                          entityId: s?.entityId,
                        };
                      }),
                    };
                  }),
              };
            }) as IProductSlotPrice[])
        : [];

      // If a given category has no prices, then no need to insert it into the DB
      if (!categoryPrices.length) {
        continue;
      }

      const memberships: IPCMembership[] = parentPCs[i].memberships?.length
        ? parentPCs[i].memberships.map((m: any) => {
            return {
              id: Number(m?.id),
              startDate: m?.startDate
                ? dayjs.utc(m?.startDate as string).toISOString()
                : "",
              expiryDate: m?.expiryDate
                ? dayjs.utc(m?.expiryDate as string).toISOString()
                : "",
              type: Number(m?.type),
              expiryType: Number(m?.expiryType),
              periodLength: Number(m?.periodLength),
              numberMandatoryPeriod: Number(m?.numberMandatoryPeriod),
            };
          })
        : [];

      const promoCodeCampaignList: IPCPromoCodeCampaignList[] = parentPCs[i]
        .promoCodeCampaignList?.length
        ? parentPCs[i].promoCodeCampaignList.map((c: any) => {
            return {
              id: Number(c.id),
              giftCardTicketId: Number(c.giftCardTicketId),
              startDateTime: String(c?.startDateTime),
              endDateTime: String(c?.endDateTime),
              expiryType: Number(c.expiryType),
              expiryDays: Number(c.expiryDays),
              cancelled: Number(c.cancelled),
            };
          })
        : [];

      const pc: IProductCategory = {
        _id: String(id),
        id: id as number,
        name: name || "",
        description: description || "",
        type: PCTypes[Number(type)],
        address: address ? address : "",
        qrCodeType,
        multipleScan: multipleScan,
        nameLocale: extractTranslation("categoryName", translations),
        descriptionLocale: extractTranslation(
          "categoryDescription",
          translations
        ),
        ticketGroups: ticketGroups?.map(
          (t: any) => t?.ticketGroupId
        ) as number[],
        product: categoryProduct,
        prices: categoryPrices,
        priceIds: categoryPrices.map((p) => p.id), // Price IDs for Dexie multi-entry index
        memberships: memberships,
        promoCodeCampaignList: promoCodeCampaignList,
        saleStartType,
        saleStart: saleStart ? dayjs.utc(saleStart).toISOString() : saleStart,
        saleEnd: saleEnd ? dayjs.utc(saleEnd).toISOString() : saleStart,
        saleEndType,
        salesEndDurationMinutes,
        salesStartDurationDays,
        timeSlotCount: timeSlotCount,
        isRecurring:
          (timeSlotCount && timeSlotCount > 1) || type === PCTypes.Recurring
            ? true
            : false,

        // Seating Plan logic: this is a parent PC so lets nullify the below fields
        seatingParentPSCId: null,
        isParentReccuringSeating: null,
        isReccuringSeating: isRecurringSeat,
        seatingParentResourceEventKey: null,
        seatingParentCategoryName: null,
        resourceEventKey: resourceRead ? resourceRead.eventKey : null,
        resource: resourceRead
          ? { quantity: Number(resourceRead.quantity) }
          : null,
      };

      // Make sure that we aren't adding any ProductCategory that was already added
      if (!seenIds.has(pc.id)) {
        seenIds.add(pc.id);
        pcs.push(pc);
      }
    }
  }

  return pcs;
}

/**
 * Takes a field name + an array of translations of possibly many fields
 * Returns an object of the field translated in 4 languages, undefined translation is possible
 */
export function extractTranslation(
  field: string,
  translations: any[] | null | undefined
): FieldLocaleType {
  const locale: FieldLocaleType = {
    en: "",
    fr: "",
    de: "",
    it: "",
    es: "",
  };
  if (translations && translations.length) {
    const filteredTranslations = translations.filter((t) => t?.field === field);

    for (let i = 0; i < SiteLanguages.length; i++) {
      locale[SiteLanguages[i]] =
        filteredTranslations.find((t) => t.locale === SiteLanguages[i])
          ?.content || "";
    }
  }
  return locale;
}

/**
 * Update a list of prices in local db (Dexie).
 * Uses the *priceIds multi-entry index to find PCs
 * @param prices - array of price IDs with the fields to update and their new values
 */
export async function updatePrices(prices: ISyncUpdates[]) {
  if (!prices.length) return;

  try {
    const updatesMap = new Map<number, ISyncUpdates>(
      prices.map((p) => [Number(p.id), p])
    );

    // Single batch query using *priceIds multi-entry index
    const priceIds = [...updatesMap.keys()];
    const matchingPCs = await getProductCategoriesByPriceIds(priceIds);

    if (!matchingPCs.length) return;

    // Apply updates: for each PC, update all its matching prices in-place
    for (const pc of matchingPCs) {
      for (const price of pc.prices) {
        const update = updatesMap.get(price.id);
        if (!update?.fields) continue;

        const updatesObj: IKeyValue = {};
        for (const field of update.fields) {
          updatesObj[field.key] = field.value;
        }

        price.updatedAt = String(updatesObj.updatedAt);
        price.quantity = Number(updatesObj.quantity);
      }
    }

    // Bulk update all PCs at once
    await bulkUpsertProductCategories(matchingPCs);

    LogService.info(
      `Done updating local stock of prices with attached parameter.`,
      { params: { newStock: prices } }
    );
  } catch (error) {
    LogService.info(
      "Error in updatePrices",
      { params: { priceCount: prices.length }, error },
      LogLevel.Error
    );
  }
}

/**
 * Get PCs related to a ticket group from local db
 */
export async function getProductCategoriesWithPricesArray(
  ticketGroupId: number,
  skip: number
): Promise<ICardData[]> {
  return converttoICardData(
    (await queryProductCategoriesByTicketGroup({
      ticketGroupId,
      skip,
      sort: true,
      excludeTypes: [PCTypes[PCTypes.SeatedSub]], // seating sub-categories are not needed in this case as they are only used for seating plans and they don't have prices attached to them
    })) || []
  );
}

/**
 * Get PCs related to a ticket group from local db where they are flattened by price
 * means one price by PC, and a PC can have as many copies as the number of prices it has
 * @returns
 */
export async function getProductCategoriesWithFlatPrice(
  ticketGroupId: number,
  skip: number
): Promise<{ cards: ICardData[]; pcCount: number }> {
  // 1- get PCs as they are from db
  const PCs =
    (await queryProductCategoriesByTicketGroup({
      ticketGroupId,
      skip,
      sort: true,
      excludeTypes: [PCTypes[PCTypes.SeatedSub]], // seating sub-categories are not needed in this case as they are only used for seating plans and they don't have prices attached to them
    })) || [];
  // 2- for each PC with X prices clone it X times and keep one price every time
  const handledPCs: IProductCategory[] = [];
  for (let i = 0; i < PCs.length; i++) {
    if (PCs[i].prices.length === 1) {
      handledPCs.push(PCs[i]);
    } else {
      // TODO Hamza: There are many prices, lets clone this PC object, as it is, many times and then keep the needed prices only
      const pricesCount = PCs[i].prices.length;
      for (let j = 0; j < pricesCount; j++) {
        // TODO Hamza: if this is retail PC and has more than one price and this price has the default price then skip it, because we need prices with not-default-retail
        if (
          pricesCount > 1 &&
          PCs[i].type === PCTypes[PCTypes.Retail] &&
          PCs[i].prices[j].retail[0].type ===
            RetailOptionType.Default_Retail_Option
        ) {
          continue;
        }
        const clonedPC = _cloneDeep(PCs[i]);
        clonedPC.prices = [];
        clonedPC.prices = [PCs[i].prices[j]];
        handledPCs.push(clonedPC);
      }
    }
  }
  // 3- run map function on the new array handledPCs as below
  return { cards: converttoICardData(handledPCs), pcCount: PCs.length };
}

/**
 * @param productCategories
 * @returns an array of ICardData elements
 */
export function converttoICardData(
  productCategories: IProductCategory[]
): ICardData[] {
  const currentLanguage = getLanguage();
  return (
    productCategories.map((pc) => {
      // IF it is seating plan product then we take the minPrice of all sub-categories
      // else we take minPricce of prices
      const minPrice =
        isSeatingParentType(pc.type) &&
        pc.subCategories &&
        pc.subCategories.length
          ? pc.subCategories
              .map((sbpc) => sbpc.prices)
              .flat()
              .sort((p1, p2) => Number(p1) - Number(p2))[0].value
          : pc.type !== PCTypes[PCTypes.Retail]
          ? pc.prices.sort((p1, p2) => Number(p1) - Number(p2))[0].value
          : pc.prices.length === 1
          ? pc.prices[0].value
          : pc.prices.find((pr) =>
              pr.retail.every(
                (rt) => rt.type !== RetailOptionType.Default_Retail_Option
              )
            )?.value;
      return {
        productCategoryId: pc.id,
        productName:
          pc.product.nameLocale[currentLanguage] ||
          pc.product.name /*pc.product.name*/,
        // Seating Plan logic
        // eventKey for the parent is the one already attached to it under the name resourceEventKey coming from backend
        // For MembershipSeated (type 17) the same resourceEventKey may contain several keys separated by comma
        eventKey:
          pc.type === PCTypes[PCTypes.SeatedSub] &&
          pc.seatingParentResourceEventKey
            ? pc.seatingParentResourceEventKey
            : (pc.type === PCTypes[PCTypes.Seated] ||
                pc.type === PCTypes[PCTypes.MembershipSeated]) &&
              pc.resourceEventKey
            ? pc.resourceEventKey
            : null,
        isReccuringSeating: pc.isReccuringSeating
          ? pc.isReccuringSeating
          : null,
        seatingParentPSCId:
          pc.type === PCTypes[PCTypes.SeatedSub] ? pc.seatingParentPSCId : null,
        seatingParentCategoryName:
          pc.type === PCTypes[PCTypes.SeatedSub]
            ? String(pc.seatingParentCategoryName)
            : "",
        // End of Seating plan logic
        categoryName: pc.nameLocale[currentLanguage] || pc.name,
        currency: pc.product.currency,
        stockIds: pc.stockIds || [],
        stockConsumptions: pc.stockConsumptions || {},
        pictureUrl:
          pc.type !== PCTypes[PCTypes.Retail]
            ? pc.product.pictureUrl
            : createImageproxyUrl(getDefaultRetailOptionSmeetzPicture(pc), {
                width: Number(CARD_IMAGE_WIDTH),
                height: Number(CARD_IMAGE_HEIGHT),
              }) || shoppingIcon,
        // to differentiate retail (merch) product categories
        type: pc.type,
        // - For tickets, take the lowest price value
        // - For merch (retail) products, take any price value that its retailOption.type !== 100
        // to avoid taking the price of the default retaiOptions created automatically by backend
        // but if we have only that default retailOption, then we should take its price
        price: minPrice,
        prices: pc.prices,
        // If this is a retail (merch) product then take the price.Quantity of the one with the default retailOption (retail.type = 100)
        // If a not-reccuring seating plan then take sumOf(every childPSC.resource.quantity)
        // If not merch product nor a not-reccuring seating plan then availabilities will be assigned later when we fetch timeslots of the ticket
        dbAvailability: ((): number => {
          // If this is a retail (merch) product then take the price.Quantity of the one with the default retailOption (retail.type = 100)
          if (pc.type === PCTypes[PCTypes.Retail]) {
            if (pc.prices.length === 1) {
              return pc.prices[0].quantity;
            } else {
              return sumArray(
                pc.prices
                  .filter((pr) =>
                    pr.retail.every(
                      (re) => re.type !== RetailOptionType.Default_Retail_Option
                    )
                  )
                  .map((pr) => pr.quantity)
              );
            }
          } else {
            // If a not-reccuring seating plan then take sumOf(every childPSC.resource.quantity)
            if (isSeatingParentType(pc.type) && !pc.isReccuringSeating) {
              const subCategoriesResourceQuantities: number[] = [];
              if (pc.subCategories && pc.subCategories.length) {
                pc.subCategories.forEach((sc) => {
                  if (sc.resource && sc.resource.quantity) {
                    subCategoriesResourceQuantities.push(sc.resource.quantity);
                  }
                });
              }
              const res = sumArray(subCategoriesResourceQuantities);
              return res;
            } else {
              // If not merch product nor a not-reccuring seating plan then availabilities will be assigned later when we fetch timeslots of the ticket
              return 0;
            }
          }
        })(),
        productCategory_ID: pc._id,
        seatedSubs: pc.subCategories ? pc.subCategories : [],
        booked: ((): number => {
          // If this is a retail (merch) product then take the price.Quantity of the one with the default retailOption (retail.type = 100)
          if (pc.type === PCTypes[PCTypes.Retail]) {
            if (pc.prices.length === 1) {
              return Math.abs(
                Math.abs(pc.prices[0].totalQuantity - pc.prices[0].quantity)
              );
            } else {
              return sumArray(
                pc.prices
                  .filter((pr) =>
                    pr.retail.every(
                      (re) => re.type !== RetailOptionType.Default_Retail_Option
                    )
                  )
                  .map((pr) => Math.abs(pr.totalQuantity - pr.quantity))
              );
            }
          } else {
            // If not merch product then availabilities will be assigned later when we fetch timeslots of the ticket
            return 0;
          }
        })(),
      };
    }) || []
  );
}

/**
 * Takes the Product Category and returns its default RetailOption.SmeetzPictures first item for picture display use
 */
function getDefaultRetailOptionSmeetzPicture(
  pc: IProductCategory
): ISmeetzPicture | undefined {
  const smeetzPicture =
    pc.prices.length === 1
      ? pc.prices[0].retail[0].smeetzPictures[0]
      : pc.prices.find((pr) =>
          pr.retail.some(
            (rt) => rt.type === RetailOptionType.Default_Retail_Option
          )
        )?.retail[0].smeetzPictures[0];

  return smeetzPicture;
}

/**
 * Take a product category and filter its prices array based on whether we have only the default retail options or we have others
 * @param product
 */
export function filterPrices(product: IProductCategory): IProductCategory {
  // Two cases:
  // 1- If ticket product we use psc.prices we get
  // 2- If merch product we filter psc.prices array so that we do not consider the default price (psp.optionType =  100) if we have other retail options,
  // otherwise, we use the default retail option if we have only one price
  if (product.type !== PCTypes[PCTypes.Retail] || product.prices.length === 1) {
    return product;
  }

  product.prices = product.prices.filter((price) =>
    price.retail.every((r) => r.type !== RetailOptionType.Default_Retail_Option)
  );
  return product;
}

/**
 * Take product (prices and type) and return true if its a retail and it has sellingSoldOut value = 1 (keep selling when sold out)
 * @param list of prices and the type of the pc
 */
export function sellOnSoldOut(
  prices: IProductSlotPrice[] | undefined,
  type: string | undefined
): boolean {
  if (!prices) return false;
  const price = prices.find((p) => p);
  const sellingSoldOut =
    type === PCTypes[PCTypes.Retail] && price && price.retail.length
      ? price.retail[0].sellingSoldOut
      : 0;
  return sellingSoldOut === PC_SELL_ON_SOLD_OUT;
}

/**
 * @param pcIds productCategories Ids that we need to get all PCs linked to the same stock entity as them
 * @returns list of categories from localDB that have same stock entity as the ones in parameters
 */
export async function getSameStockPCs(pcIds: string[]) {
  if (!pcIds.length) return [];

  try {
    const numericIds = pcIds.map((id) => parseInt(id, 10));
    const dbCategories = await getProductCategoriesByIds(numericIds);
    if (!dbCategories.length) return [];

    // Collect unique stockIds using Set + flatMap
    const stockIds = [
      ...new Set(
        dbCategories.flatMap((c) =>
          (c.stockIds || []).filter(Boolean).map(Number)
        )
      ),
    ];

    return await getProductCategoriesByStockIds(stockIds);
  } catch (error) {
    LogService.info(
      "Error in getSameStockPCs",
      { pcIds, error },
      LogLevel.Error
    );
    return [];
  }
}

/**
 * 1- Receives parent categories with needed seating plan data, and an array linking those parent PCs to their children PCs
 * 2- Gets PCs (sub-categories) from localDB
 * 3- Update those PCs objects and returns them
 */
async function getAndUpdateSubCategories(
  seatingPSCsChildren: { childPSCId: number; parentPSCId: number }[],
  parentPSCs: any[]
): Promise<IProductCategory[]> {
  const updatedSubCategories: IProductCategory[] = [];

  const subCategoriesFromLocalDB = await getProductCategoriesByIds(
    seatingPSCsChildren.map((el) => el.childPSCId)
  );

  // Build Map for O(1) parent lookup instead of O(N*M) with .find()
  const parentPSCsMap = new Map<number, any>(parentPSCs.map((p) => [p.id, p]));

  for (let i = 0; i < subCategoriesFromLocalDB.length; i++) {
    const parentPSC = parentPSCsMap.get(
      subCategoriesFromLocalDB[i].seatingParentPSCId!
    );
    if (!parentPSC) continue;

    const updatedSubPC = cloneDeep(
      subCategoriesFromLocalDB[i] as IProductCategory
    );
    updatedSubPC.isParentReccuringSeating = parentPSC.isRecurringSeat;
    updatedSubPC.seatingParentCategoryName = parentPSC.name;
    updatedSubPC.seatingParentResourceEventKey =
      parentPSC.resourceRead.eventKey;
    updatedSubCategories.push(updatedSubPC);
  }
  return updatedSubCategories;
}

/**
 * Fetches smeez pictures for a given entity type (SmeetzPictureEntityTypes) and entity ids
 * @param params
 */
export async function fetchAndStoreSmeetzPictures(params: {
  entityIds: number[];
  entityType: SmeetzPictureEntityTypes;
}) {
  const { entityIds, entityType } = params;
  const entityTable = SmeetzPictureEntityTypeToTableMap[entityType];

  try {
    LogService.info(
      `Picture: fetching ${entityType} pictures for ${entityIds.length} entities`
    );

    const pictures = await getSmeetzPictures(entityType, entityIds);

    const batchSize = 5;
    for (let i = 0; i < pictures.length; i += batchSize) {
      const batch = pictures.slice(i, i + batchSize);
      await Promise.all(
        batch.map(async (picture) => {
          try {
            const url = createCroppedImageproxyUrl({
              name: picture?.name,
              table: entityTable,
              metaData: picture?.metaData,
            });

            const response = await fetch(url);
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }
            const blob = await response.blob();

            await dexieDB.pictures.put({
              id: Number(picture?.id),
              entityType,
              entityId: Number(picture?.entityId),
              imageBlob: blob,
              timestamp: dayjs().toISOString(),
            });
          } catch (error) {
            LogService.info(
              `Picture: failed to fetch ${entityType} picture for entity ${picture?.entityId}`,
              { error },
              LogLevel.Error
            );
          }
        })
      );
    }
  } catch (error) {
    LogService.info(
      `Picture: error when fetching ${entityType} pictures`,
      { error },
      LogLevel.Error
    );
  }
}

/**
 * Fetches smeez pictures for a given entity type (SmeetzPictureEntityTypes) and entity ids
 * @param params
 *
 * TODO @AYMEN: remove this function and use fetchAndStoreSmeetzPictures instead
 */
export async function fetchAndStoreGroupPicture(
  id: number,
  url: string,
  name: string
) {
  try {
    LogService.info(`Picture: Pulling group picture with ${id}`);

    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const blob = await response.blob();

      await dexieDB.pictures.put({
        id: id,
        entityType: SmeetzPictureEntityTypes.Group,
        entityId: id,
        imageBlob: blob,
        timestamp: dayjs().toISOString(),
      });
    } catch (error) {
      LogService.info(
        `Picture: failed to fetch group picture with ${id}`,
        { error },
        LogLevel.Error
      );
    }
  } catch (error) {
    LogService.info(
      `Picture: error when fetching group picture with ${id}`,
      { error },
      LogLevel.Error
    );
  }
}

/**
 * Helper function to call fetchAndStoreGroupPicture
 */
export async function proceedFetchAndStoreGroupPicture(signal?: AbortSignal) {
  // Fetch all group logo blobs into memory first, before touching Dexie
  let groupLogos: any[] = [];
  if (
    POSConfigModule.groupInfo &&
    POSConfigModule.groupInfo.brandingList &&
    POSConfigModule.groupInfo.brandingList.length &&
    POSConfigModule.groupInfo.brandingList[0] &&
    POSConfigModule.groupInfo.brandingList[0].brandingPictureList
  ) {
    groupLogos = POSConfigModule.groupInfo.brandingList[0].brandingPictureList;
  }

  const fetchedPictures: {
    id: number;
    entityType: number;
    entityId: number;
    imageBlob: Blob;
    timestamp: string;
  }[] = [];

  for (const logo of groupLogos) {
    if (!logo || signal?.aborted) continue;
    try {
      LogService.info(`Picture: Pulling group picture with ${Number(logo.Id)}`);
      const response = await fetch(String(logo.url), { signal });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const blob = await response.blob();
      fetchedPictures.push({
        id: Number(logo.Id),
        entityType: SmeetzPictureEntityTypes.Group,
        entityId: Number(logo.Id),
        imageBlob: blob,
        timestamp: dayjs().toISOString(),
      });
    } catch (error) {
      LogService.info(
        `Error while fetching group picture with id ${Number(logo.Id)}`,
        error,
        LogLevel.Error
      );
    }
  }

  if (signal?.aborted) return;

  // Atomically replace group pictures in Dexie:
  // delete old group pictures and insert new ones in a single transaction
  await (dexieDB as any).transaction("rw", dexieDB.pictures, async () => {
    await dexieDB.pictures
      .where({ entityType: SmeetzPictureEntityTypes.Group })
      .delete();
    if (fetchedPictures.length > 0) {
      await dexieDB.pictures.bulkPut(fetchedPictures);
    }
  });
}

/**
 * Ensures addon product categories are loaded in Dexie.
 *
 * Some prices have addons that may not have been loaded even after loading the POS config (not part of any sales collection).
 * This function streams through all local PCs to find addon price IDs that are missing,
 * then fetches and stores those PCs from the remote DB.
 *
 * Note: These addon PCs are loaded for booking purposes but are not displayed for individual sale.
 */
export async function checkAndFetchPCAddons() {
  // check if the app is online before start fetching
  if (!window.navigator.onLine) {
    LogService.info(`App is not online, stop fetching PC addons`);
    return;
  }

  try {
    // Stream through PCs to collect addon price IDs and existing price IDs
    // without loading the entire table into memory
    const allAddonPriceIds = new Set<number>();
    const allExistingPriceIds = new Set<number>();

    await dexieDB.productCategories.each((pc) => {
      for (const price of pc.prices) {
        allExistingPriceIds.add(price.id);
        for (const addon of price.addons) {
          if (addon.canceled !== 1) {
            allAddonPriceIds.add(addon.priceId);
          }
        }
      }
    });

    // Find addon prices that are not yet loaded locally
    const pricesToLoad: number[] = [];
    for (const addonPriceId of allAddonPriceIds) {
      if (!allExistingPriceIds.has(addonPriceId)) {
        pricesToLoad.push(addonPriceId);
      }
    }

    if (!pricesToLoad.length) {
      return;
    }

    LogService.info("Going to load missing addons", { pricesToLoad });

    const limit = 100;
    let skip = 0;

    // eslint-disable-next-line
    while (true) {
      const res = await getProductCategoriesIdsByPriceIds(pricesToLoad, {
        skip,
        limit,
      });
      const prices = res.productSlotPrices;

      if (!prices || !prices.length) {
        break;
      }

      // filter out non-number values
      const pcIds = prices.flatMap((p) =>
        p?.productCategory?.id ? [p.productCategory.id] : []
      );

      let pcs = (await getPCListWithSeatingPlanData(pcIds)).productCategory;

      if (!pcs || !pcs.length) {
        break;
      }

      // update the offset before filtering
      skip += prices.length;

      // consider only not-cancelled prices
      pcs = pcs.filter(
        (pc) =>
          pc?.cancelled === 0 && pc?.product?.status === ProductStatus.LIVE
      );
      // going to convert to Local ProductCategories
      const localPCs = await toLocalProductCategories3(pcs, []);

      const localPCIds = localPCs.map((pc) => pc.id);
      // fetch the Skidata Handshake properties (to be used when creating the qrcode)
      const handshakeCategoryTspropertyList =
        await getHandshakeCategoryTspropertyList(localPCIds);

      // Build Map for O(1) lookup instead of O(N*M) with .find()
      const handshakeMap = new Map(
        handshakeCategoryTspropertyList.map((config) => [
          Number(config?.categoryId),
          config,
        ])
      );

      for (const pc of localPCs) {
        const skidataConfig = handshakeMap.get(pc.id);
        pc.handshakeCategoryTsproperty = skidataConfig
          ? {
              areaCode: String(skidataConfig?.areaCode),
              areaName: String(skidataConfig?.areaName),
              ticketTypeCode: String(skidataConfig?.ticketTypeCode),
              ticketTypeName: String(skidataConfig?.ticketTypeName),
            }
          : null;
      }

      // going to insert them in db
      await bulkUpsertProductCategories(localPCs);

      // fetch and store all the product logos (pictures) to be printed on the ticket
      const productIds = localPCs.map((pc) => pc.product.id);
      await fetchAndStoreSmeetzPictures({
        entityType: SmeetzPictureEntityTypes.ProductLogo,
        entityIds: productIds,
      });

      // fetch and store all the ticket (PC) logos (pictures) to be printed on the ticket
      await fetchAndStoreSmeetzPictures({
        entityType: SmeetzPictureEntityTypes.TicketLogo,
        entityIds: localPCIds,
      });
    }
  } catch (error) {
    LogService.info(
      "Error in checkAndFetchPCAddons",
      { error },
      LogLevel.Error
    );
  }
}

/**
 * 1- Pull concerned timeslots from remote
 * 3- Update the timeslots values in DB
 * @param bookings
 */
export async function syncUpdatedRetails(
  pssList: IProductSlotState[]
): Promise<void> {
  if (!pssList.length) {
    return;
  }

  const priceIdsSet = new Set<number>();
  const stockIdsSet = new Set<number>();
  for (const pss of pssList) {
    if (pss.bookingStatus !== BookingStatus.Pending || !!pss.timeSlotId) {
      continue;
    }

    priceIdsSet.add(pss.priceId);
    if (pss.stockIds && pss.stockIds.length) {
      pss.stockIds.forEach((sid) => stockIdsSet.add(sid));
    }
  }

  // get all categories linked to these stockIds
  const stockIds = [...stockIdsSet];
  try {
    const dbCategories = await getProductCategoriesByStockIds(stockIds);
    dbCategories.forEach((pc) => {
      pc.prices.forEach((p) => priceIdsSet.add(p.id));
    });
  } catch (error) {
    LogService.info(
      `Error when fetching product categories by stockIds: ${[
        ...stockIds,
      ]} from local db.`,
      {
        params: {
          stockIds: stockIds,
          error: error,
        },
      },
      LogLevel.Error
    );
  }

  const priceIds = [...priceIdsSet];
  if (!priceIds.length) {
    return;
  }
  // Pull concerned prices from remote db
  const updatedPrices = await getUpdatedRetailPrices(priceIds);
  if (!updatedPrices?.length) {
    return;
  }

  // build updates array
  const updates: ISyncUpdates[] = updatedPrices.map((ts) => {
    const tsFields: ISyncField[] = Object.entries(ts)
      .filter(([prop]) => !["id", "categoryId", "__typename"].includes(prop))
      .map(([key, value]) => ({ key, value: String(value) }));

    return { id: String(ts.id), fields: tsFields };
  });

  await updatePrices(updates);
}
